import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '../components/Button';
import { signOut, useUser } from '../auth/session';
import { useDb } from '../db/hooks';
import { startCheckout } from '../lib/api';
import { fetchEntitlement } from './entitlement';

const features = [
  'Sessions, vitals, fluid and weight',
  'Labs with trends and flags',
  'Medications and calendar reminders',
  'Doctor report PDF',
  'Works offline, synced across your devices'
];

const plans = [
  { id: 'yearly', name: 'Yearly', price: '$59', period: 'per year', note: 'Renews automatically. Cancel any time.' },
  { id: 'lifetime', name: 'Lifetime', price: '$150', period: 'one-time', note: 'Pay once, use it for good.' }
] as const;

type PlanId = (typeof plans)[number]['id'];

const POLL_MS = 2_000;
const POLL_FOR_MS = 60_000;

/**
 * Shown to signed-in accounts without a plan (SPEC.md §6.3). Back from Stripe with
 * ?checkout=success, it waits for the webhook to record the purchase, then lets the app open.
 */
export function PaywallPage({ onCheckAgain }: { onCheckAgain: () => Promise<void> }) {
  const user = useUser();
  const db = useDb();
  const params = new URLSearchParams(window.location.search);
  const preselected = params.get('plan') as PlanId | null;
  const [returning, setReturning] = useState(params.get('checkout') === 'success');
  const [busy, setBusy] = useState<PlanId | 'check' | null>(null);
  const [error, setError] = useState<string | null>(params.get('checkout') === 'cancelled' ? 'Checkout was cancelled. You haven’t been charged.' : null);

  useEffect(() => {
    if (!returning) return;
    let stopped = false;
    const started = Date.now();
    const poll = async () => {
      while (!stopped && Date.now() - started < POLL_FOR_MS) {
        try {
          const entitlement = await fetchEntitlement(db);
          if (entitlement.state !== 'none') {
            window.history.replaceState(null, '', '/');
            await onCheckAgain();
            return;
          }
        } catch {
          // Offline for a moment; keep trying until the time runs out.
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      }
      if (!stopped) {
        setReturning(false);
        setError('Your payment went through, but your plan hasn’t arrived yet. It usually takes a few seconds: tap “check again” in a moment. If it still doesn’t appear, email support@getrenalflow.com.');
      }
    };
    void poll();
    return () => {
      stopped = true;
    };
  }, [returning, db, onCheckAgain]);

  async function buy(plan: PlanId) {
    setBusy(plan);
    setError(null);
    try {
      await startCheckout(plan);
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : String(checkoutError));
      setBusy(null);
    }
  }

  async function checkAgain() {
    setBusy('check');
    await onCheckAgain();
    setBusy(null);
  }

  if (returning) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6 text-center">
        <div className="max-w-sm space-y-4">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-clinical-primary" />
          <h1 className="text-xl font-semibold">Thank you! Setting up your plan…</h1>
          <p className="text-sm text-clinical-muted">This takes a few seconds.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="safe-top mx-auto max-w-lg px-4 py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Choose your plan</h1>
      <p className="mt-1 text-sm text-clinical-muted">Signed in as {user.email}</p>

      <ul className="mt-5 space-y-2">
        {features.map((feature) => (
          <li key={feature} className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-clinical-primary" /> {feature}
          </li>
        ))}
      </ul>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`rounded-xl border bg-clinical-card p-5 shadow-soft ${preselected === plan.id ? 'border-clinical-primary ring-2 ring-clinical-primary/30' : 'border-clinical-line'}`}
          >
            <h2 className="font-semibold">{plan.name}</h2>
            <p className="mt-2">
              <span className="text-3xl font-bold">{plan.price}</span> <span className="text-sm text-clinical-muted">{plan.period}</span>
            </p>
            <p className="mt-1 text-xs text-clinical-muted">{plan.note}</p>
            <Button className="mt-4 w-full" variant={preselected && preselected !== plan.id ? 'secondary' : 'primary'} disabled={busy !== null} onClick={() => void buy(plan.id)}>
              {busy === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy === plan.id ? 'Opening checkout…' : `Get ${plan.name.toLowerCase()}`}
            </Button>
          </div>
        ))}
      </div>
      <p className="mt-3 text-center text-xs text-clinical-muted">60-day money-back guarantee on your first purchase, no questions asked. Prices may include local tax.</p>
      {error ? <p className="mt-3 rounded-lg bg-amber-500/10 p-3 text-sm">{error}</p> : null}

      <div className="mt-8 flex flex-col gap-2">
        <Button variant="secondary" onClick={() => void checkAgain()} disabled={busy !== null}>
          {busy === 'check' ? 'Checking…' : 'I’ve already paid: check again'}
        </Button>
        <a href="/?demo=1" className="focus-ring rounded-lg py-2.5 text-center text-sm font-semibold text-clinical-primary hover:underline">
          Try the demo first
        </a>
        <Button variant="ghost" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
